import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaFacebookF,
  FaWhatsapp,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { IoWallet } from "react-icons/io5";
import styles from "./sidebar.module.css";
import { logoutUser } from "../../Services/api";
import {
  loadHome,
  loadProducts,
  loadOrders,
  loadSubscriptions,
} from "../../routeLoaders";

import InstallAppModal from "../../Components/InstallAppModal";

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("magizh_user") || "null");
    } catch {
      return null;
    }
  });

  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const updateUser = () => {
      try {
        setCurrentUser(JSON.parse(localStorage.getItem("magizh_user") || "null"));
      } catch {
        setCurrentUser(null);
      }
    };

    updateUser();

    window.addEventListener("storage", updateUser);
    window.addEventListener("magizh-user-updated", updateUser);

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    return () => {
      window.removeEventListener("storage", updateUser);
      window.removeEventListener("magizh-user-updated", updateUser);
    };
  }, [location.pathname]);

  const isActive = (path) => {
    const targetPath = path.split("?")[0];
    const isCategoryBrowse =
      location.pathname === "/products" &&
      new URLSearchParams(location.search).has("category");

    if (
      targetPath === "/home" &&
      (location.pathname === "/" ||
        location.pathname === "/home" ||
        isCategoryBrowse)
    ) {
      return true;
    }

    if (targetPath === "/products" && isCategoryBrowse) {
      return false;
    }

    return location.pathname === targetPath;
  };

  const handleLogout = () => {
    logoutUser();
    if (onClose) onClose();
    navigate("/welcome", { replace: true });
  };

  const getGuideUrl = () => {
    if (process.env.REACT_APP_GUIDE_URL) {
      return process.env.REACT_APP_GUIDE_URL;
    }
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");

    if (isLocal) {
      return "http://localhost:3001";
    }

    const hostname = window.location.hostname;
    if (hostname.includes("magizhone.in")) {
      return "https://guide.magizhone.in";
    }

    return `https://guide.${hostname.replace(/^www\./, "")}`;
  };

  const handleInstallApp = () => {
    setIsInstallModalOpen(true);
  };

  const navItems = [
    {
      label: "Explore",
      path: "/home",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 11.5 12 4l9 7.5" />
          <path d="M5.5 10.5V21h13V10.5" />
          <path d="M9.5 21v-6h5v6" />
        </svg>
      ),
    },
    {
      label: "All Products",
      path: "/products",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      ),
    },
    {
      label: "Prime Member",
      path: "/prime-member",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" />
        </svg>
      ),
    },
    {
      label: "My Purchase",
      path: "/orders",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 8V6a6 6 0 0 1 12 0v2" />
          <path d="M4 8h16l1 13H3L4 8Z" />
        </svg>
      ),
    },
    {
      label: "Plan & Renewals",
      path: "/subscriptions",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 7v5h-5" />
          <path d="M4 17v-5h5" />
          <path d="M6.1 9A7 7 0 0 1 18.4 6L20 7" />
          <path d="M17.9 15A7 7 0 0 1 5.6 18L4 17" />
        </svg>
      ),
    },
  ];

  const routeLoaders = {
    "/home": loadHome,
    "/products": loadProducts,
    "/orders": loadOrders,
    "/subscriptions": loadSubscriptions,
  };

  const walletBalance = Number(currentUser?.wallet_balance || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={onClose}
          aria-label="Close sidebar"
        />
      )}

      <aside className={`${styles.sidebar} ${isOpen ? styles.mobileOpen : ""}`}>
        <div className={styles.sidebarGlow} />

        <div className={styles.sidebarHeader}>
          <Link to="/home" className={styles.brandLogo} onClick={onClose}>
            <img
              src={`${process.env.PUBLIC_URL}/Assets/logo.png`}
              alt="Magizh One"
            />
          </Link>

          <p className={styles.brandTagline}>ONE PLATFORM, MANY SOLUTIONS</p>

          <button
            type="button"
            className={styles.mobileCloseBtn}
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <nav className={styles.menuList} aria-label="Main navigation">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              onPointerEnter={routeLoaders[item.path]}
              onFocus={routeLoaders[item.path]}
              onTouchStart={routeLoaders[item.path]}
              className={`${styles.menuItem} ${isActive(item.path) ? styles.activeItem : ""}`}
              onClick={onClose}
            >
              <span className={styles.itemIcon}>{item.icon}</span>
              <span className={styles.itemLabel}>{item.label}</span>
              {item.badge && <span className={styles.primeBadge}>{item.badge}</span>}
            </Link>
          ))}
        </nav>

        {/* Download App & Magizh Guide Action Buttons */}
        <div className={styles.downloadSection}>
          <button
            type="button"
            className={styles.downloadAppBtn}
            onClick={handleInstallApp}
          >
            <div className={styles.downloadIconWrapper}>
              {isStandalone ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </div>
            <div className={styles.downloadText}>
              <span className={styles.downloadTitle}>
                {isStandalone ? "App Installed" : "Download App"}
              </span>
              <span className={styles.downloadSub}>
                {isStandalone ? "Magizh One" : "Install for Mobile & Desktop"}
              </span>
            </div>
            <svg
              className={styles.actionArrow}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <a
            href={getGuideUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.guideAppBtn}
            onClick={onClose}
          >
            <div className={styles.guideIconWrapper}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
              </svg>
            </div>
            <div className={styles.downloadText}>
              <span className={styles.downloadTitle}>Magizh Guide</span>
              <span className={styles.downloadSub}>Guides &amp; Tutorials</span>
            </div>
            <svg
              className={styles.actionArrow}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </a>
        </div>

        <div className={styles.menuDivider} />

        <div className={styles.followUsSection}>
          <h4 className={styles.followUsTitle}>FOLLOW US ON</h4>
          <div className={styles.socialGrid}>
            <a
              href="https://www.facebook.com/share/1GXgGqtqgj/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialItem}
              aria-label="Facebook"
            >
              <div className={styles.socialIcon}>
                <FaFacebookF />
              </div>
              <span className={styles.socialLabel}>Facebook</span>
            </a>

            <a
              href="https://whatsapp.com/channel/0029VbB9B9FDTkKC1pemuQ2i"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialItem}
              aria-label="WhatsApp"
            >
              <div className={styles.socialIcon}>
                <FaWhatsapp />
              </div>
              <span className={styles.socialLabel}>WhatsApp</span>
            </a>

            <a
              href="https://www.instagram.com/magizh_one?stkn=OTI1dXZhczB0NTBo"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialItem}
              aria-label="Instagram"
            >
              <div className={styles.socialIcon}>
                <FaInstagram />
              </div>
              <span className={styles.socialLabel}>Instagram</span>
            </a>

            <a
              href="https://youtube.com/@magizhinfotech?si=YfPtTtwmgUpZ2Hrl"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialItem}
              aria-label="YouTube"
            >
              <div className={styles.socialIcon}>
                <FaYoutube />
              </div>
              <span className={styles.socialLabel}>YouTube</span>
            </a>
          </div>
        </div>

        <div className={styles.sidebarBottom}>
          <Link
            to="/profile"
            className={styles.sidebarWalletCard}
            onClick={onClose}
          >
            <div className={styles.sidebarWalletIcon}>
              <IoWallet size={19} />
            </div>
            <div className={styles.sidebarWalletInfo}>
              <span className={styles.sidebarWalletLabel}>Available Cashback</span>
              <strong className={styles.sidebarWalletAmount}>₹ {walletBalance}</strong>
            </div>
            <svg
              className={styles.userArrow}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>

          <button type="button" className={styles.logoutBtn} onClick={handleLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Dedicated App Install Modal */}
      <InstallAppModal
        isOpen={isInstallModalOpen}
        onClose={() => setIsInstallModalOpen(false)}
      />
    </>
  );
};

export default Sidebar;
